/**
 * Tema claro/escuro (RNF15).
 *
 * Os tokens de cor em index.css ja respondem a `prefers-color-scheme` e ao
 * atributo `data-theme` do <html>; este modulo so decide qual dos dois manda.
 * "Sistema" e o padrao: o lojista que nunca mexeu recebe o tema do proprio
 * aparelho. A escolha explicita fica no localStorage e e aplicada ANTES do
 * primeiro render (main.tsx chama `applyStoredTheme` antes de montar o React)
 * para nao piscar claro-escuro na abertura.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'cf.tema';

export const THEME_OPTIONS: readonly { readonly value: ThemeChoice; readonly label: string }[] = [
  { value: 'system', label: 'Sistema' },
  { value: 'light', label: 'Claro' },
  { value: 'dark', label: 'Escuro' },
];

export function readThemeChoice(): ThemeChoice {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'light' || raw === 'dark' ? raw : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', choice);
  }
  // A barra do navegador no celular acompanha a superficie da pagina.
  const dark = choice === 'dark' || (choice === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    meta.setAttribute('content', dark ? '#0d0d0d' : '#f9f9f7');
  }
}

export function storeThemeChoice(choice: ThemeChoice): void {
  try {
    if (choice === 'system') {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, choice);
    }
  } catch {
    // Sem armazenamento a escolha vale so nesta aba.
  }
}

export function applyStoredTheme(): void {
  applyTheme(readThemeChoice());
}

/** O tema resolvido de fato, para quem precisa saber (ex.: cor do grafico). */
export function resolvedTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') {
    return choice;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
